import React, { useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  alpha,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { formatAmount } from "../../../../utils/hooks/dataHandler";
import EmptySummary from "../CaSchedules/utils/EmptySummary";
import {
  CameraExportButton,
  exportComponentAsImage,
} from "../../../../utils/export/componentExporter";

const AdditionSummary = ({ summaryData, yearOfAssessment }) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const exportRef = useRef(null);
  const handleExportAsImage = async (e) => {
    e.stopPropagation();
    if (exportRef.current) {
      await exportComponentAsImage(exportRef.current, "FA Additions Summary");
    }
  };

  if (!summaryData || Object.keys(summaryData).length === 0) {
    return <EmptySummary caType='FA Additions' dataType='an addition' />;
  }

  return (
    <Paper
      ref={exportRef}
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        bgcolor: (theme) => alpha(theme.palette.background.paper, 0.98),
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ p: 3 }}>
          <Typography variant='h6' fontWeight={600} color='primary.main'>
            FA Additions Summary - Year of Assessment {yearOfAssessment}
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
            Breakdown of additions (Deferred vs Non-Deferred)
          </Typography>
        </Box>
        <CameraExportButton
          onClick={handleExportAsImage}
          tooltipTitle='Screenshot of the FA Additions Summary'
          sx={{ mr: 4 }}
        />
      </Box>

      {/* Summary Table */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow
              sx={{
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              }}
            >
              <TableCell
                sx={{
                  fontWeight: 600,
                  fontSize: isSmallScreen ? "0.875rem" : "1rem",
                }}
              >
                CA Name
              </TableCell>
              <TableCell
                align='center'
                sx={{
                  fontWeight: 600,
                  fontSize: isSmallScreen ? "0.875rem" : "1rem",
                }}
              >
                No. of years of claim
              </TableCell>
              <TableCell
                align='right'
                sx={{
                  fontWeight: 600,
                  fontSize: isSmallScreen ? "0.875rem" : "1rem",
                }}
              >
                Deferred
              </TableCell>
              <TableCell
                align='right'
                sx={{
                  fontWeight: 600,
                  fontSize: isSmallScreen ? "0.875rem" : "1rem",
                }}
              >
                Non-Deferred
              </TableCell>
              <TableCell
                align='right'
                sx={{
                  fontWeight: 600,
                  fontSize: isSmallScreen ? "0.875rem" : "1rem",
                }}
              >
                Total
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.keys(summaryData).map((caName, caIndex) => {
              const yearGroups = summaryData[caName];
              const caNameTotal = Object.values(yearGroups).reduce(
                (sum, { deferred, nonDeferred }) =>
                  sum + deferred + nonDeferred,
                0
              );

              return (
                <React.Fragment key={caName}>
                  {/* Year rows */}
                  {Object.keys(yearGroups).map((year, idx) => {
                    const { deferred, nonDeferred } = yearGroups[year];
                    return (
                      <TableRow
                        key={`${caName}-${year}`}
                        sx={{
                          "&:hover": {
                            bgcolor: (theme) =>
                              alpha(theme.palette.primary.main, 0.04),
                          },
                        }}
                      >
                        <TableCell
                          sx={{
                            fontWeight: idx === 0 ? 600 : 400,
                            color: idx === 0 ? "primary.main" : "text.primary",
                            pl: idx === 0 ? 3 : 6,
                          }}
                        >
                          {idx === 0 ? caName : ""}
                        </TableCell>
                        <TableCell align='center'>{year}</TableCell>
                        <TableCell align='right'>
                          {formatAmount(deferred)}
                        </TableCell>
                        <TableCell align='right'>
                          {formatAmount(nonDeferred)}
                        </TableCell>
                        <TableCell align='right'>
                          {formatAmount(deferred + nonDeferred)}
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {/* CA Name Total */}
                  <TableRow
                    sx={{
                      bgcolor: (theme) =>
                        alpha(theme.palette.primary.main, 0.04),
                      borderTop: "2px solid",
                      borderColor: (theme) => alpha(theme.palette.divider, 0.3),
                    }}
                  >
                    <TableCell
                      sx={{ fontWeight: 700, color: "primary.main", pl: 3 }}
                    >
                      Total for {caName}
                    </TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                    <TableCell
                      align='right'
                      sx={{ fontWeight: 700, color: "primary.main" }}
                    >
                      {formatAmount(caNameTotal)}
                    </TableCell>
                  </TableRow>

                  {/* Spacer row */}
                  {caIndex < Object.keys(summaryData).length - 1 && (
                    <TableRow>
                      <TableCell
                        colSpan={5}
                        sx={{
                          borderBottom: "1px dashed",
                          borderColor: "divider",
                          height: 8,
                          p: 0,
                        }}
                      />
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default AdditionSummary;
